import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { formatMoney } from '../../utils/money';
import { formatDateTime as fmtDateTimeCT } from '../../utils/dateUtils';
import EditRequestModal from '../components/EditRequestModal';
import { useIsMobile, BottomSheet, Icon } from '../../mobile';

const STATUS_COLORS = {
  submitted: '#3b82f6',
  processing: '#f59e0b',
  ready_for_payment: '#8b5cf6',
  completed: '#10b981',
  denied: '#ef4444',
  already_released: '#f59e0b',
  bid_sent: '#10b981',
  forwarded: '#10b981',
  approved: '#10b981',
  scheduled: '#8b5cf6',
  published: '#0891b2',
  cancelled: '#64748b',
};
// Gate Access reads its steps, not generic request statuses.
const GATE_STATUS_LABELS = {
  submitted: 'Assign Rep',
  processing: 'With Rep',
  completed: 'Access Granted',
};
// The rep's closeout picks (requests.gate_access_type / gate_resolution) —
// mirrors gateAccessService.
const GATE_ACCESS_TYPES = { code: 'Gate code', app: 'App', clicker: 'Clicker / remote', toll_tag: 'Toll tag', other: 'Other' };
const GATE_RESOLUTIONS = { parkingsnap: 'Updated in ParkingSnap', driver: 'Provided to Driver' };
const STATUS_LABELS = {
  submitted: 'Submitted',
  processing: 'Processing',
  ready_for_payment: 'Ready for Payment',
  completed: 'Completed',
  denied: 'Denied',
  already_released: 'Already Released',
  bid_sent: 'Bid Sent',
  forwarded: 'Forwarded to Titan',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'On Route',
  cancelled: 'Cancelled',
};

// Work-type label map per vendor-style bucket. The custom Details grid + form
// share these labels.
const WORK_LABELS_BY_BUCKET = {
  striping_bid: {
    striping: 'Striping',
    pressure_washing: 'Pressure Washing',
    both: 'Both (Striping + Pressure Washing)',
  },
  valet_trash: {
    valet: 'Valet Trash',
    bulk:  'Bulk Trash',
    both:  'Both (Valet + Bulk)',
  },
};
const CUSTOM_FORM_BUCKETS = new Set(Object.keys(WORK_LABELS_BY_BUCKET));

// Shared CT-pinned formatter from utils/dateUtils. Local wrapper preserves
// the existing call-site API (default to the standard date+time format).
function formatDateTime(s) {
  if (!s) return '—';
  return fmtDateTimeCT(s, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export default function RequestDetailPage() {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { user, isSuperAdmin, can } = useAuth();
  const isAdmin = isSuperAdmin || (typeof can === 'function' && can('manage_admin'));
  const currentUserId = user?.id;
  const [request, setRequest] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeUsers, setActiveUsers] = useState([]);
  const [editing, setEditing] = useState(false);
  const isMobile = useIsMobile();
  const [options, setOptions] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Investigation form state (bad_tow only, admin-visible)
  const [invDriver, setInvDriver] = useState('');
  const [invAmount, setInvAmount] = useState('');
  const [invOutcome, setInvOutcome] = useState('');
  const [invNotes, setInvNotes] = useState('');
  const [savingInv, setSavingInv] = useState(false);

  async function loadRequest() {
    try {
      const data = await api.getRequest(id);
      setRequest(data);
      // Fire timeline fetch in parallel — non-blocking, errors are non-fatal
      api.getRequestTimeline(id).then(setTimeline).catch(() => setTimeline(null));
      // Prefill investigation form from existing row
      setInvDriver(data.driver_user_id || '');
      setInvAmount(data.deduction_amount || '');
      setInvOutcome(data.investigation_outcome || '');
    } catch (err) {
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadRequest(); }, [id]);
  useEffect(() => { if (isAdmin) api.getActiveUsers().then(setActiveUsers).catch(() => {}); }, [isAdmin]);

  async function saveInvestigation(e) {
    e.preventDefault();
    setSavingInv(true);
    try {
      await api.investigateRequest(id, {
        driver_user_id: invDriver || null,
        deduction_amount: invAmount !== '' ? Number(invAmount) : null,
        outcome: invOutcome || null,
        notes: invNotes.trim() || null,
      });
      setInvNotes('');
      await loadRequest();
    } catch (err) {
      alert(err.message || 'Save failed');
    } finally {
      setSavingInv(false);
    }
  }

  async function handleDelete() {
    const ok = window.confirm(
      `Permanently delete ${request.type_label || request.type} #${request.id}?\n\n` +
      'This removes the submission, its Task Board card(s), and any spawned bad-tow ' +
      'investigation. Photos and documents are deleted too. This cannot be undone.'
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await api.deleteRequest(request.id);
      navigate(`/sales/requests/${type}`, { replace: true });
    } catch (err) {
      alert(err.message || 'Delete failed');
      setDeleting(false);
    }
  }

  if (loading) return <p style={{ padding: 24, color: 'var(--text-muted)' }}>Loading…</p>;
  if (error || !request) return <div className="admin-page"><p className="alert alert-error">{error || 'Not found'}</p></div>;

  const photos = safeParse(request.photo_urls);
  const docs = safeParse(request.doc_urls);

  // Edit rules mirror the server: submitter while still pending; admins (and
  // the Sign Manager, for sign requests) until a terminal status. bad_tow
  // rows are system-spawned — the investigation form is their edit surface.
  const EDITABLE_STATUSES = ['submitted', 'scheduled'];
  const isOwnPending = request.submitted_by === currentUserId && EDITABLE_STATUSES.includes(request.status);
  const TERMINAL_STATUSES = ['completed', 'denied', 'already_released', 'cancelled', 'bid_sent', 'forwarded', 'approved'];
  const isSignManager = user?.position_name === 'Sign Manager';
  const isTaggingManager = user?.position_name === 'Tagging Manager';
  const ownsFieldJob = (request.type === 'sign_request' && isSignManager)
    || (request.type === 'tagging_request' && isTaggingManager);
  // Gate Access is worked entirely from its Task Board card (assign rep →
  // access type → Access Granted), so there's no edit form for it.
  const canEdit = !['bad_tow', 'gate_access'].includes(request.type)
    && (isOwnPending
      || ((isAdmin || ownsFieldJob) && !TERMINAL_STATUSES.includes(request.status)));

  const statusPill = (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 8,
      background: `${STATUS_COLORS[request.status]}15`, color: STATUS_COLORS[request.status],
      textTransform: 'uppercase', letterSpacing: 0.5, whiteSpace: 'nowrap',
    }}>
      {(request.type === 'gate_access' && GATE_STATUS_LABELS[request.status]) || STATUS_LABELS[request.status] || request.status}
    </span>
  );

  return (
    <div className="admin-page" style={{ maxWidth: 900 }}>
      {/* Phone header: back + options icons on one row, large title under
          it, status pill on its own line. Edit / Delete live in a sheet so
          the header never overflows. */}
      {isMobile ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <button type="button" className="m-btn-icon sm" aria-label="Back" onClick={() => navigate(`/sales/requests/${type}`)}><Icon name="back" size={20} /></button>
            {(canEdit || isSuperAdmin) && (
              <button type="button" className="m-btn-icon sm" aria-label="Options" onClick={() => setOptions(true)}><Icon name="more" size={22} /></button>
            )}
          </div>
          <h1 className="m-title" style={{ fontSize: 24 }}>{request.type_label || type} #{request.id}</h1>
          <p className="m-sub">Submitted {formatDateTime(request.created_at)} by {request.submitter_name}</p>
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            {statusPill}
            {request.is_emergency ? <span className="m-pill" style={{ background: '#fef2f2', color: '#dc2626' }}>EMERGENCY</span> : null}
          </div>
          <BottomSheet open={options} title={`${request.type_label || type} #${request.id}`} onClose={() => setOptions(false)}>
            <div className="m-list">
              {canEdit && <button type="button" className="m-btn m-btn-secondary" onClick={() => { setOptions(false); setEditing(true); }}>Edit submission</button>}
              {isSuperAdmin && (
                <button type="button" className="m-btn m-btn-secondary" disabled={deleting} style={{ color: '#dc2626', borderColor: '#fecaca' }}
                  onClick={() => { setOptions(false); handleDelete(); }}>{deleting ? 'Deleting…' : 'Delete submission'}</button>
              )}
            </div>
          </BottomSheet>
        </div>
      ) : (
      <>
      <button onClick={() => navigate(`/sales/requests/${type}`)}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        ← Back to {request.type_label || type}
      </button>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
        <div>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {request.type_icon && <span style={{ fontSize: 22 }}>{request.type_icon}</span>}
            <span>{request.type_label || type} #{request.id}</span>
            {request.is_emergency ? (
              <span style={{ fontSize: 10, fontWeight: 800, color: '#fff', background: '#ef4444', padding: '3px 10px', borderRadius: 6 }}>
                🚨 EMERGENCY
              </span>
            ) : null}
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            Submitted {formatDateTime(request.created_at)} by {request.submitter_name}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {canEdit && (
            <button className="btn btn-muted btn-sm" onClick={() => setEditing(true)}>
              ✏️ Edit
            </button>
          )}
          {isSuperAdmin && (
            <button className="btn btn-sm" onClick={handleDelete} disabled={deleting}
              style={{ background: '#fff', border: '1px solid #dc262650', color: '#dc2626', fontWeight: 600 }}>
              {deleting ? 'Deleting…' : '🗑 Delete'}
            </button>
          )}
          {statusPill}
        </div>
      </div>
      </>
      )}

      {editing && (
        <EditRequestModal
          request={request}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); loadRequest(); }}
        />
      )}

      {/* Details grid — Sponsored Event has its own minimal layout
          (property / date / requestor); vendor buckets get the work-type
          grid; everything else falls back to the tow-style grid. */}
      {['sign_request', 'tagging_request'].includes(request.type) ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <DetailBox label="Property" value={request.account_name || '—'} />
          <DetailBox label="Region" value={(request.type === 'tagging_request' ? request.tagging_region : request.sign_region) || '—'} />
          <DetailBox label="Work Requested" value={(request.type === 'tagging_request' ? request.tagging_work_summary : request.sign_work_summary) || '—'} />
          <DetailBox
            label="Estimated Time"
            value={request.estimated_minutes
              ? (request.estimated_minutes >= 60 ? `${request.estimated_minutes / 60} hour${request.estimated_minutes > 60 ? 's' : ''}` : `${request.estimated_minutes} minutes`)
              : '—'}
          />
          <DetailBox
            label="Work Date"
            value={request.scheduled_date
              ? new Date(request.scheduled_date + 'T12:00:00Z').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })
              : '🚨 Awaiting scheduling'}
          />
          <DetailBox label="Work Address" value={request.work_address || '—'} />
          {request.access_instructions ? (
            <DetailBox label="Access Instructions" value={request.access_instructions} />
          ) : null}
          {request.special_sign_description ? (
            <DetailBox label="Custom / Special Sign" value={request.special_sign_description} />
          ) : null}
          {request.special_tagging_description ? (
            <DetailBox label="Special Tagging Instructions" value={request.special_tagging_description} />
          ) : null}
          {request.type === 'tagging_request' && request.tagging_frequency && request.tagging_frequency !== 'single' ? (
            <DetailBox
              label="Tagging Frequency"
              value={`🔁 ${request.tagging_frequency === 'monthly' ? 'Monthly' : 'Quarterly'} — visit #${request.tagging_occurrence || 1}`}
            />
          ) : null}
          {request.type === 'tagging_request' && request.tagging_due_date ? (
            <DetailBox
              label="Next Visit Due"
              value={new Date(`${request.tagging_due_date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
            />
          ) : null}
        </div>
      ) : request.type === 'gate_access' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <DetailBox label="Property" value={request.account_name || '—'} />
          <DetailBox label="Rep" value={request.gate_rep_name || 'Not assigned yet'} />
          <DetailBox
            label="Next Step"
            value={request.status === 'submitted' ? 'Super admin assigns a rep'
              : request.status === 'processing' ? (request.gate_access_type ? 'Rep marks Access Granted (reminded every 24h)' : 'Rep picks the access type (reminded every 24h)')
              : request.status === 'completed' ? 'Done — driver was texted'
              : '—'}
          />
          {request.gate_access_type ? (
            <DetailBox label="Access Type" value={GATE_ACCESS_TYPES[request.gate_access_type] || request.gate_access_type} />
          ) : null}
          {request.gate_resolution ? (
            <DetailBox label="Access Granted" value={GATE_RESOLUTIONS[request.gate_resolution] || request.gate_resolution} />
          ) : null}
        </div>
      ) : request.type === 'item_request' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <DetailBox label="Property" value={request.account_name || '—'} />
          <DetailBox label="Item Type" value={request.item_type_other ? `Other: ${request.item_type_other}` : (request.reason_name || request.reason || '—')} />
          <DetailBox
            label="Number of Items"
            value={request.item_count != null ? Number(request.item_count).toLocaleString('en-US') : '—'}
          />
        </div>
      ) : request.type === 'sponsored_event' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <DetailBox label="Property" value={request.account_name || '—'} />
          <DetailBox
            label="Requested Date"
            value={request.requested_date
              ? new Date(request.requested_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
              : '—'}
          />
          <DetailBox label="Requestor" value={request.requestor_name || '—'} />
        </div>
      ) : CUSTOM_FORM_BUCKETS.has(request.type) ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <DetailBox
            label="Work Type"
            value={(WORK_LABELS_BY_BUCKET[request.type] || {})[request.work_request_type] || request.work_request_type || '—'}
          />
          <DetailBox
            label="Timeline"
            value={request.requested_timeline === 'emergency' ? '🚨 Emergency' : (request.requested_timeline === 'standard' ? 'Standard' : '—')}
          />
          <DetailBox
            label="Property"
            value={
              <div>
                <div>{request.account_name || '—'}</div>
                {request.property_address_other && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                    {request.property_address_other}
                  </div>
                )}
              </div>
            }
          />
          <DetailBox
            label="Requestor"
            value={
              <div>
                <div>{request.requestor_name || '—'}</div>
                {request.requestor_email && (
                  <a href={`mailto:${request.requestor_email}`}
                     style={{ fontSize: 12, color: '#3b82f6', textDecoration: 'none' }}>
                    {request.requestor_email}
                  </a>
                )}
              </div>
            }
          />
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
          <DetailBox label="Account" value={request.account_name || '—'} />
          <DetailBox label="Vehicle" value={request.vehicle_info || '—'} />
          <DetailBox label="Reason" value={request.reason_name || request.reason || '—'} />
          {request.refund_category && (
            <DetailBox
              label="Refund Amount"
              value={
                request.refund_category === 'other' && request.amount_requested != null
                  ? `Other — ${formatMoney(request.amount_requested)}`
                  : request.refund_category.charAt(0).toUpperCase() + request.refund_category.slice(1)
              }
            />
          )}
        </div>
      )}

      {/* Sponsored Event — render scope_of_work under the "Event Details" label */}
      {request.type === 'sponsored_event' && request.scope_of_work && (
        <Section title="Event Details">
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--text-primary)' }}>{request.scope_of_work}</div>
        </Section>
      )}

      {/* Vendor buckets — Scope of Work + optional Map */}
      {CUSTOM_FORM_BUCKETS.has(request.type) && request.scope_of_work && (
        <Section title="Scope of Work">
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--text-primary)' }}>{request.scope_of_work}</div>
        </Section>
      )}
      {CUSTOM_FORM_BUCKETS.has(request.type) && request.map_url && (
        <Section title="Map">
          <a href={request.map_url} target="_blank" rel="noopener noreferrer"
             style={{ display: 'inline-block' }}>
            {/\.(png|jpe?g|gif|webp)$/i.test(request.map_url) ? (
              <img
                src={request.map_url}
                alt="Property map"
                style={{
                  maxWidth: 360, maxHeight: 240, borderRadius: 8,
                  border: '1px solid var(--border-subtle)', objectFit: 'contain', background: '#fff',
                }}
              />
            ) : (
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)',
                borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontWeight: 600,
              }}>📎 View map file</span>
            )}
          </a>
        </Section>
      )}

      {/* Timeline — every state transition with timestamp + actor + duration */}
      {timeline && timeline.events && timeline.events.length > 0 && (
        <Section title="Timeline">
          <TimelinePanel timeline={timeline} />
        </Section>
      )}

      {/* Notes */}
      {request.notes && (
        <Section title={request.type === 'gate_access' ? "What's not working" : 'Notes'}>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: 'var(--text-primary)' }}>{request.notes}</div>
        </Section>
      )}

      {/* No Charge → "Already Released" hand-off: submitter can kick off a refund */}
      {request.type === 'no_charge' && request.status === 'already_released'
        && request.submitted_by === currentUserId && (
        <Section title="Vehicle Was Already Released">
          <div style={{
            padding: 14, borderRadius: 8,
            background: '#fef3c7', border: '1px solid #f59e0b40',
          }}>
            <div style={{ fontSize: 13, color: '#78350f', marginBottom: 10 }}>
              Dispatch couldn't apply the no-charge — the vehicle had already been released
              (customer paid). If a refund is appropriate, you can open one now and it'll carry
              over the property, vehicle, and notes from this request.
            </div>
            <button
              className="btn btn-gold"
              onClick={() => navigate(`/sales/requests/refund/new?from=${request.id}`)}
              style={{ fontWeight: 700 }}
            >
              💵 Request a Refund →
            </button>
          </div>
        </Section>
      )}

      {/* Generic parent-link — any row that was spawned from another request */}
      {request.parent_request && request.type !== 'bad_tow' && (
        <Section title="Continuation">
          <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
              This request was opened as a follow-up to an earlier one.
            </div>
            <button
              className="btn btn-sm btn-muted"
              onClick={() => navigate(`/sales/requests/${request.parent_request.type}/${request.parent_request.id}`)}
            >
              ← Open {request.parent_request.type_label || request.parent_request.type} #{request.parent_request.id}
            </button>
          </div>
        </Section>
      )}

      {/* Take Back — spawned children progress (after GM approval) */}
      {request.type === 'take_back' && (request.spawned_call_id || request.no_charge_task_item_id) && (
        <Section title="Take Back Progress">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {request.spawned_call_id && (
              <ProgressRow
                icon="🚚"
                label="Dispatch Call"
                done={!!request.call_resolved_at}
                doneAt={request.call_resolved_at}
                linkText={`Open Dispatch Call #${request.spawned_call_id}`}
                linkHref="/dispatch"
              />
            )}
            {request.no_charge_task_item_id && (
              <ProgressRow
                icon="📄"
                label="No-Charge Paperwork"
                done={!!request.no_charge_done_at}
                doneAt={request.no_charge_done_at}
                linkText={`Open No-Charge Task #${request.no_charge_task_item_id}`}
                linkHref={`/workflow/items/${request.no_charge_task_item_id}`}
              />
            )}
            {(!request.call_resolved_at || !request.no_charge_done_at) && (request.status !== 'denied') && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                The request will auto-complete when both are done.
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Bad Tow flag on a non-bad_tow request — links to child investigation for admins */}
      {request.type !== 'bad_tow' && request.bad_tow ? (
        <Section title="⚠️ Flagged as Bad Tow">
          <div style={{
            padding: 14, borderRadius: 8, border: '1px solid #dc262640', background: '#fff1f2',
          }}>
            <div style={{ fontSize: 13, color: '#7f1d1d', fontWeight: 600, marginBottom: 6 }}>
              Reason submitted:
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-primary)', whiteSpace: 'pre-wrap', marginBottom: 10 }}>
              {request.bad_tow_reason || '(no reason provided)'}
            </div>
            {request.bad_tow_investigation ? (
              <button
                className="btn btn-sm"
                onClick={() => navigate(`/sales/requests/bad_tow/${request.bad_tow_investigation.id}`)}
                style={{ background: '#dc2626', color: '#fff', fontWeight: 600 }}
              >
                Open Bad Tow Investigation #{request.bad_tow_investigation.id} →
              </button>
            ) : (
              <div style={{ fontSize: 12, color: '#7f1d1d' }}>
                An investigation has been opened. {isAdmin ? 'Link will appear when ready.' : 'Only the GM can view it.'}
              </div>
            )}
          </div>
        </Section>
      ) : null}

      {/* Parent request link on a bad_tow investigation */}
      {request.type === 'bad_tow' && request.parent_request && (
        <Section title="Triggered By">
          <div style={{ padding: 12, borderRadius: 8, background: 'var(--bg-subtle)', border: '1px solid var(--border-subtle)' }}>
            <button
              className="btn btn-sm btn-muted"
              onClick={() => navigate(`/sales/requests/${request.parent_request.type}/${request.parent_request.id}`)}
            >
              ← Open {request.parent_request.type_label} #{request.parent_request.id}
            </button>
          </div>
        </Section>
      )}

      {/* GM-only investigation form on bad_tow detail */}
      {request.type === 'bad_tow' && isAdmin && (
        <Section title="Investigation">
          <form onSubmit={saveInvestigation} style={{
            padding: 16, borderRadius: 8, border: '1px solid var(--border-subtle)', background: '#fff',
            display: 'flex', flexDirection: 'column', gap: 12,
          }}>
            <div>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Driver</label>
              <select className="form-input" value={invDriver} onChange={e => setInvDriver(e.target.value)}
                style={{ width: '100%', padding: '8px 12px' }}>
                <option value="">— Select driver —</option>
                {activeUsers
                  .filter(u => /driver/i.test(u.role || '') || /driver/i.test(u.position_name || ''))
                  .concat(activeUsers.filter(u => !/driver/i.test(u.role || '') && !/driver/i.test(u.position_name || '')))
                  .map(u => (
                    <option key={u.id} value={u.id}>
                      {u.full_name}{u.position_name ? ` — ${u.position_name}` : ''}
                    </option>
                  ))}
              </select>
            </div>

            <div>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>
                Deduction Amount ($)
              </label>
              <input type="number" step="0.01" min="0" className="form-input"
                value={invAmount} onChange={e => setInvAmount(e.target.value)}
                placeholder="e.g. 50.00"
                style={{ width: 200, padding: '8px 12px' }} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Amount to deduct from the driver's next paycheck if outcome is Approved.
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Outcome</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {[
                  { key: 'approved',  label: 'Approved',  color: '#10b981' },
                  { key: 'denied',    label: 'Denied',    color: '#ef4444' },
                  { key: 'dismissed', label: 'Dismissed', color: '#64748b' },
                ].map(opt => {
                  const selected = invOutcome === opt.key;
                  return (
                    <button key={opt.key} type="button" onClick={() => setInvOutcome(opt.key)}
                      style={{
                        padding: '8px 18px', fontSize: 13, fontWeight: 700, borderRadius: 8,
                        border: `2px solid ${selected ? opt.color : 'var(--border-subtle)'}`,
                        background: selected ? `${opt.color}15` : '#fff',
                        color: selected ? opt.color : 'var(--text-muted)',
                        cursor: 'pointer',
                      }}>{opt.label}</button>
                  );
                })}
              </div>
            </div>

            <div>
              <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>
                Investigation Notes (optional)
              </label>
              <textarea className="form-input" rows={3}
                value={invNotes} onChange={e => setInvNotes(e.target.value)}
                placeholder="What did you find? Any context worth keeping on the record…"
                style={{ width: '100%', padding: '8px 12px', resize: 'vertical' }} />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button type="submit" className="btn btn-gold" disabled={savingInv} style={{ fontWeight: 700 }}>
                {savingInv ? 'Saving…' : 'Save Investigation'}
              </button>
            </div>
          </form>
          {request.driver_name && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
              Currently assigned to driver: <strong>{request.driver_name}</strong>
              {request.deduction_amount != null && ` · Deduction: ${formatMoney(request.deduction_amount)}`}
              {request.investigation_outcome && ` · Outcome: ${request.investigation_outcome}`}
            </div>
          )}
        </Section>
      )}

      {/* Photos */}
      {photos.length > 0 && (
        <Section title={`Photos (${photos.length})`}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {photos.map((url, i) => (
              <a key={i} href={url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'block', width: 120, height: 120, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--border-subtle)' }}>
                <img src={url} alt={`photo-${i}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </a>
            ))}
          </div>
        </Section>
      )}

      {/* Legacy doc_urls (for migrated refunds) */}
      {docs.length > 0 && (
        <Section title="Documents">
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {docs.map((url, i) => (
              <li key={i}><a href={url} target="_blank" rel="noopener noreferrer">{url.split('/').pop()}</a></li>
            ))}
          </ul>
        </Section>
      )}

      {/* Workflow items linked to this request */}
      {request.workflow_items?.length > 0 && (
        <Section title="Workflow">
          {request.workflow_items.map(wi => (
            <div key={wi.id} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: 12, border: '1px solid var(--border-subtle)', borderRadius: 8, marginBottom: 8,
              background: 'var(--bg-subtle)',
            }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{wi.title}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  Current stage: <span style={{ color: wi.stage_color, fontWeight: 600 }}>{wi.stage_name}</span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                <button className="btn btn-muted btn-sm"
                  onClick={() => navigate(`/workflow/tree/${wi.id}`)}
                  title="This request and every sub-task it started">
                  View tree
                </button>
                <button className="btn btn-muted btn-sm"
                  onClick={() => navigate(`/workflow/items/${wi.id}`)}>
                  Open Task
                </button>
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* Refund-specific payment info */}
      {request.type === 'refund' && request.status === 'completed' && (
        <Section title="Payment">
          <DetailRow label="Check #" value={request.check_number || '—'} />
          <DetailRow label="Amount Paid" value={request.payment_amount ? formatMoney(request.payment_amount) : '—'} />
          <DetailRow label="Method" value={request.payment_method || '—'} />
          <DetailRow label="Paid On" value={formatDateTime(request.paid_at)} />
          {request.payment_notes && <DetailRow label="Notes" value={request.payment_notes} />}
        </Section>
      )}

      {/* Denial info */}
      {request.status === 'denied' && (
        <Section title="Denial">
          <DetailRow label="Reason" value={request.denial_reason || '—'} />
          <DetailRow label="Denied By" value={request.denier_name || '—'} />
          <DetailRow label="Denied On" value={formatDateTime(request.denied_at)} />
        </Section>
      )}
    </div>
  );
}

function DetailBox({ label, value }) {
  return (
    <div style={{ padding: 12, background: 'var(--bg-subtle)', borderRadius: 8, border: '1px solid var(--border-subtle)' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)', marginTop: 4 }}>{value}</div>
    </div>
  );
}
function DetailRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 10, padding: '6px 0', fontSize: 13 }}>
      <span style={{ color: 'var(--text-muted)', minWidth: 110 }}>{label}:</span>
      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
    </div>
  );
}
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <h3 style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>
        {title}
      </h3>
      {children}
    </div>
  );
}
function safeParse(s) {
  if (!s) return [];
  if (Array.isArray(s)) return s;
  try { return JSON.parse(s) || []; } catch { return []; }
}

function ProgressRow({ icon, label, done, doneAt, linkText, linkHref }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: 12,
      borderRadius: 8, border: `1px solid ${done ? '#10b98140' : 'var(--border-subtle)'}`,
      background: done ? '#f0fdf4' : 'var(--bg-subtle)',
    }}>
      <span style={{ fontSize: 20 }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>{label}</div>
        <div style={{ fontSize: 12, color: done ? '#065f46' : 'var(--text-muted)', marginTop: 2 }}>
          {done
            ? `✓ Done${doneAt ? ` ${fmtDateTimeCT(doneAt, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}` : ''}`
            : '⏳ Pending'}
        </div>
      </div>
      {linkHref && (
        <a href={linkHref}
          style={{
            fontSize: 12, fontWeight: 600, color: '#4f46e5', textDecoration: 'none',
            padding: '4px 10px', borderRadius: 6, border: '1px solid #4f46e530',
          }}>
          {linkText} →
        </a>
      )}
    </div>
  );
}

// ─── Timeline ────────────────────────────────────────────────
// Mirrors the Call detail Timeline panel. Renders an event log of
// every state transition: Submitted → Processing → Paid/Denied,
// plus take-back child progress and workflow_item_history rows.

const EVENT_ICONS = {
  submitted: '📝',
  processed: '⚙️',
  paid: '💵',
  denied: '🚫',
  tow_completed: '🚚',
  no_charge_done: '📄',
  investigation_outcome: '🔍',
  workflow_transition: '➡️',
};
const EVENT_COLORS = {
  submitted: '#3b82f6',
  processed: '#f59e0b',
  paid: '#10b981',
  denied: '#ef4444',
  tow_completed: '#10b981',
  no_charge_done: '#10b981',
  investigation_outcome: '#7c3aed',
  workflow_transition: '#64748b',
};

function fmtDuration(seconds) {
  if (!seconds || seconds < 60) return seconds ? `${Math.round(seconds)}s` : '—';
  const m = Math.floor(seconds / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 0) return `${d}d ${h % 24}h`;
  if (h > 0) return `${h}h ${m % 60}m`;
  return `${m}m`;
}

function TimelinePanel({ timeline }) {
  const { events, total_seconds } = timeline;
  return (
    <div>
      {total_seconds > 0 && (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
          End-to-end: <strong style={{ color: 'var(--text-primary)' }}>{fmtDuration(total_seconds)}</strong>
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {events.map((e, i) => {
          const prev = i > 0 ? events[i - 1] : null;
          const stepSec = prev && e.at && prev.at
            ? (new Date(e.at) - new Date(prev.at)) / 1000
            : 0;
          const color = EVENT_COLORS[e.kind] || '#64748b';
          return (
            <div key={i} style={{
              display: 'grid', gridTemplateColumns: '36px 1fr auto', gap: 10, alignItems: 'flex-start',
              padding: 12, borderRadius: 8, background: '#fff',
              border: `1px solid ${color}25`, borderLeft: `4px solid ${color}`,
            }}>
              <div style={{ fontSize: 20, lineHeight: 1, marginTop: 2 }}>{EVENT_ICONS[e.kind] || '•'}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
                  {e.label}
                  {e.terminal && (
                    <span style={{
                      marginLeft: 8, fontSize: 10, fontWeight: 700,
                      padding: '2px 6px', borderRadius: 4,
                      background: '#10b98115', color: '#065f46',
                      textTransform: 'uppercase',
                    }}>final</span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                  {e.at ? fmtDateTimeCT(e.at, {
                    month: 'short', day: 'numeric', year: 'numeric',
                    hour: 'numeric', minute: '2-digit',
                  }) : '—'}
                  {e.actor_name ? <> · <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{e.actor_name}</span></> : null}
                </div>
                {e.note && (
                  <div style={{
                    marginTop: 6, padding: '6px 10px', borderRadius: 6,
                    background: 'var(--bg-subtle)', fontSize: 12,
                    color: 'var(--text-primary)', whiteSpace: 'pre-wrap',
                  }}>{e.note}</div>
                )}
              </div>
              {i > 0 && stepSec > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap', alignSelf: 'center' }}>
                  +{fmtDuration(stepSec)}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
